import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable max-lines-per-function */
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormFooter, FormSection } from '@atlaskit/form';
import { STORED_EXTERNAL_TO_ATLASSIAN, STORED_IN_ATLASSIAN_AND_DR_SUPPORTED, SUPPORTS_DATA_RESIDENCY } from '@atlassian/mpac-api-client';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { ExternalLink, SectionHeading } from '@atlassian/mpac-ui';
import { useLocalStorage } from '@atlassian/mpac-utils/hooks';
import { unflattenObject } from '@atlassian/mpac-utils/object';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_CONTACT_SUPPORT, ANALYTICS_DISCARD_CHANGES, ANALYTICS_DISCARD_CHANGES_CONFIRMATION_MODAL, ANALYTICS_SAVE_AND_PREVIEW, ANALYTICS_SCREEN_INTRODUCTION_MODAL_VIEW, getUiErroredInputNames } from '../../analytics';
import { Section } from '../../common/Section';
import { usePrivacySecurityAnalytics } from '../../hooks/usePrivacySecurityAnalytics';
import DiscardChangesModal from '../DiscardChangesModal';
import DraftStatus from '../DraftStatus/DraftStatus';
import IntroductionModal from '../IntroductionModal';
import { appTrustDataTransformer } from './appTrustDataTransformer';
import PartnerFormSectionMessages from './components/PartnerFormSectionMessages';
import QuestionInputSection from './components/QuestionInputSection';
import { partnerFormQuestionsConfig } from './config/partnerFormConfig';
import { partnerFormQuestionsConfigWithDaReChanges } from './config/partnerFormConfigWithDaReChanges';
import { useRenderDependentQuestion } from './hooks/useRenderDependentQuestion';
import { BannerWrapper, FormContainer, Header, Subcategory, SubcategoryHeading, SubHeader } from './styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// re-filling the form-values which was deleted on submit for BE
export const refillDataResidencyOption = formValue => {
  if (formValue['dataResidency.isDataResidencySupported'] === STORED_EXTERNAL_TO_ATLASSIAN || formValue['dataResidency.isDataResidencySupported'] === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) {
    formValue['dataResidency.isInScopeDataStoredOutsideAtlassian'] = formValue['dataResidency.isDataResidencySupported'];
    formValue['dataResidency.isDataResidencySupported'] = SUPPORTS_DATA_RESIDENCY;
  }
  return formValue;
};
const PrivacyAndSecurityForm = _ref => {
  let {
    draftAppTrustData,
    liveAppTrustData,
    onSave,
    onDiscard
  } = _ref;
  const appTrustData = draftAppTrustData || liveAppTrustData;
  // TODO: Create form data type
  const {
    enableForgeRemoteDataResidencyChanges
  } = useStatsig();
  const [formData, setFormData] = useState({});
  const [isDiscardDisabled, setIsDiscardDisabled] = useState(true);
  const updatedPartnerFormQuestionsConfig = enableForgeRemoteDataResidencyChanges() ? partnerFormQuestionsConfigWithDaReChanges : partnerFormQuestionsConfig;
  const {
    parentQueOnChange,
    hiddenQuestions
  } = useRenderDependentQuestion(appTrustData);
  const {
    fireScreenEvent,
    fireButtonEvent,
    addScreenAttributes
  } = usePrivacySecurityAnalytics();
  const showDraftLabel = draftAppTrustData && appTrustData;
  const [showIntroductionModal, setshowIntroductionModal] = useState(false);
  const initFormData = () => {
    if (appTrustData) {
      let transformedFormValue = appTrustDataTransformer(appTrustData);
      if (enableForgeRemoteDataResidencyChanges()) {
        transformedFormValue = refillDataResidencyOption(transformedFormValue);
      }
      Object.keys(transformedFormValue).forEach(queKey => {
        parentQueOnChange(queKey, transformedFormValue[queKey], transformedFormValue);
      });
      setFormData(transformedFormValue);
    }
  };
  const {
    state: privacyAndSecurityModalState,
    isLocalStorageSupported,
    writeState
  } = useLocalStorage('privacyAndSecurityIntroModal', false);
  useEffect(() => {
    initFormData();
    if (isLocalStorageSupported && privacyAndSecurityModalState === false && !appTrustData) {
      setshowIntroductionModal(true);
      writeState(true);
      fireScreenEvent(ANALYTICS_SCREEN_INTRODUCTION_MODAL_VIEW);
    }
  }, []);
  const resetForm = async () => {
    fireButtonEvent(ANALYTICS_DISCARD_CHANGES_CONFIRMATION_MODAL);
    setIsDiscardDisabled(true);

    // reset data if discard was successful
    if (await onDiscard()) {
      initFormData();
    }
  };
  const onChange = (questionKey, value) => {
    if (isDiscardDisabled) {
      setIsDiscardDisabled(false);
    }
    const newFormValue = _objectSpread({}, formData);
    newFormValue[questionKey] = value;
    parentQueOnChange(questionKey, value, newFormValue);
    setFormData(newFormValue);
  };
  const onSubmit = () => {
    if (enableForgeRemoteDataResidencyChanges() && formData['dataResidency.isInScopeDataStoredOutsideAtlassian'] !== undefined) {
      formData['dataResidency.isDataResidencySupported'] = formData['dataResidency.isInScopeDataStoredOutsideAtlassian'];
      delete formData['dataResidency.isInScopeDataStoredOutsideAtlassian'];
    }
    const unflattenedFormData = unflattenObject(formData);
    const finalFormData = {
      appTrustInformation: _objectSpread({}, unflattenedFormData)
    };
    return onSave(finalFormData);
  };
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => {
    fireButtonEvent(ANALYTICS_DISCARD_CHANGES);
    setIsOpen(true);
  };
  const closeModal = () => setIsOpen(false);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showIntroductionModal && /*#__PURE__*/_jsx(IntroductionModal, {}), isOpen && /*#__PURE__*/_jsx(DiscardChangesModal, {
      openModal: isOpen,
      closeModal: closeModal,
      onDiscard: resetForm
    }), /*#__PURE__*/_jsxs(FormContainer, {
      children: [/*#__PURE__*/_jsx(Header, {
        children: "Privacy & Security Details"
      }), /*#__PURE__*/_jsxs(BannerWrapper, {
        children: [/*#__PURE__*/_jsx(PartnerFormSectionMessages, {
          appTrustData: liveAppTrustData || draftAppTrustData,
          addScreenAttributes: addScreenAttributes
        }), showDraftLabel && /*#__PURE__*/_jsx(DraftStatus, {
          timestamp: appTrustData.properties.updatedAt,
          name: appTrustData.properties.updatedBy
        })]
      }), /*#__PURE__*/_jsxs(SubHeader, {
        children: ["Need help in filling the form?", ' ', /*#__PURE__*/_jsxs(ExternalLink, {
          href: "https://ecosystem.atlassian.net/servicedesk/customer/portal/34/group/110/create/654",
          onClick: () => {
            fireButtonEvent(ANALYTICS_CONTACT_SUPPORT);
          },
          children: ["Contact support team", ' ']
        }), ' ']
      }), /*#__PURE__*/_jsx(Form, {
        onSubmit: onSubmit,
        children: _ref2 => {
          let {
            formProps,
            submitting
          } = _ref2;
          return /*#__PURE__*/_jsxs("form", _objectSpread(_objectSpread({}, formProps), {}, {
            children: [/*#__PURE__*/_jsx(FormSection, {
              children: updatedPartnerFormQuestionsConfig.map(_ref3 => {
                let {
                  heading,
                  subcategory
                } = _ref3;
                return /*#__PURE__*/_jsxs("div", {
                  children: [/*#__PURE__*/_jsx(Section, {
                    children: /*#__PURE__*/_jsx(SectionHeading, {
                      children: heading
                    })
                  }), subcategory.map((_ref4, i) => {
                    let {
                      heading: subheading,
                      questions
                    } = _ref4;
                    return /*#__PURE__*/_jsxs(Subcategory, {
                      children: [subheading && /*#__PURE__*/_jsx(SubcategoryHeading, {
                        children: subheading
                      }), /*#__PURE__*/_jsx(QuestionInputSection, {
                        questions: questions,
                        hiddenQuestions: hiddenQuestions,
                        onChange: onChange,
                        formData: formData
                      })]
                    }, i);
                  })]
                }, heading);
              })
            }), /*#__PURE__*/_jsx(FormFooter, {
              align: "start",
              children: /*#__PURE__*/_jsxs(ButtonGroup, {
                children: [/*#__PURE__*/_jsx(LoadingButton, {
                  type: "submit",
                  appearance: "primary",
                  isLoading: submitting,
                  onClick: () => {
                    // Form validations has to run before we check for validation errors
                    // which are handled by the child components
                    setTimeout(() => {
                      const uiErroredInputNames = getUiErroredInputNames(formProps.ref);
                      if (uiErroredInputNames && uiErroredInputNames.length > 0) {
                        fireButtonEvent(ANALYTICS_SAVE_AND_PREVIEW, {
                          uiErroredInputNames
                        });
                      }
                    }, 0);
                  },
                  children: "Save and preview"
                }), /*#__PURE__*/_jsx(Button, {
                  onClick: openModal,
                  isDisabled: isDiscardDisabled && !draftAppTrustData // if there is no change and no draft
                  ,
                  children: "Discard changes"
                })]
              })
            })]
          }));
        }
      })]
    })]
  });
};
export default PrivacyAndSecurityForm;